import { Delete01Icon, PlusSignIcon } from "@hugeicons-pro/core-stroke-rounded";
import { SectionCard } from "shared/app/LeadsTableView/components/CreateLeadModal/SectionCard";
import { useFormController } from "shared/app/LeadsTableView/components/CreateLeadModal/useFormController";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Input,
  InputSize,
  Show
} from "shared/components";

export const ContactInfoSection = ({
  form
}: {
  form: ReturnType<typeof useFormController>;
}) => {
  const phoneContacts = form.values.contact_methods.filter(
    (cm) => cm.type === "phone"
  );
  const emailContacts = form.values.contact_methods.filter(
    (cm) => cm.type === "email"
  );

  const getContactMethodIndex = (
    type: "phone" | "email",
    localIndex: number
  ) => {
    const contactsOfType = form.values.contact_methods.filter(
      (cm) => cm.type === type
    );
    const targetContact = contactsOfType[localIndex];
    return form.values.contact_methods.findIndex((cm) => cm === targetContact);
  };

  return (
    <SectionCard title="Contact Information" wrapperClassName="!p-0">
      <div className="flex w-full">
        {/* Phone Numbers Section */}
        <div className="flex-1 flex flex-col gap-4 pb-8 pt-6 px-8 border-r border-alpha-40">
          <div className="font-medium text-sm leading-5 text-content-700">
            Phone numbers
          </div>
          <div className="flex flex-col gap-4">
            {phoneContacts.map((contact, localIndex) => {
              const globalIndex = getContactMethodIndex("phone", localIndex);
              const isTheOnlyItem = localIndex === 0;

              return (
                <div
                  key={globalIndex}
                  className={`flex ${isTheOnlyItem ? "gap-4" : "gap-2"} items-center`}
                >
                  <div className="flex-1">
                    <Input
                      id={`phone-${globalIndex}`}
                      value={contact.value}
                      onChange={(e) =>
                        form.updateContactMethod(globalIndex, e.target.value)
                      }
                      error={form.register("contact_methods").error}
                      size={InputSize.SM}
                      placeholder="(123) 456-7890"
                      type="tel"
                      className="!mb-0"
                    />
                  </div>
                  {!isTheOnlyItem && (
                    <IconButton
                      variant={IconButtonVariant.BASE}
                      size={IconButtonSize.SM}
                      HugeIcon={Delete01Icon}
                      onClick={() => form.removeContactMethod(globalIndex)}
                    />
                  )}
                </div>
              );
            })}
          </div>
          <div className="border-t border-alpha-40 pt-4">
            <Button
              size={ButtonSize.SM}
              HugeIcon={PlusSignIcon}
              variant={ButtonVariant.BASE}
              className="w-full"
              onClick={() => form.addContactMethod("phone")}
            >
              Add phone number
            </Button>
          </div>
        </div>

        {/* Emails Section */}
        <div className="flex-1 flex flex-col gap-4 pb-8 pt-6 px-8">
          <div className="font-medium text-sm leading-5 text-content-700">
            Emails
          </div>
          <div className="flex flex-col gap-4">
            {emailContacts.map((contact, localIndex) => {
              const globalIndex = getContactMethodIndex("email", localIndex);
              const isTheOnlyItem = localIndex === 0;

              return (
                <div
                  key={globalIndex}
                  className={`flex ${isTheOnlyItem ? "gap-4" : "gap-2"} items-center`}
                >
                  <div className="flex-1">
                    <Input
                      id={`email-${globalIndex}`}
                      value={contact.value}
                      onChange={(e) =>
                        form.updateContactMethod(globalIndex, e.target.value)
                      }
                      size={InputSize.SM}
                      placeholder="john.doe@acme.com"
                      type="email"
                      className="!mb-0"
                    />
                  </div>
                  {!isTheOnlyItem && (
                    <IconButton
                      variant={IconButtonVariant.BASE}
                      size={IconButtonSize.SM}
                      HugeIcon={Delete01Icon}
                      onClick={() => form.removeContactMethod(globalIndex)}
                    />
                  )}
                </div>
              );
            })}
          </div>
          <div className="border-t border-alpha-40 pt-4">
            <Button
              size={ButtonSize.SM}
              HugeIcon={PlusSignIcon}
              variant={ButtonVariant.BASE}
              className="w-full"
              onClick={() => form.addContactMethod("email")}
            >
              Add email
            </Button>
          </div>
        </div>
      </div>
      <Show if={Boolean(form.register("contact_methods").error)}>
        <div className="px-6 pb-4">
          <div className="text-sm text-red-600">
            {form.register("contact_methods").error}
          </div>
        </div>
      </Show>
    </SectionCard>
  );
};
